import { useEffect, useSyncExternalStore } from 'react';
import { v2Api } from './api/v2Client';
import bundledLogoBlack from '../assets/courtly-logo-black.svg';
import bundledLogoWhite from '../assets/courtly-logo-white.svg';
import { PLATFORM_NAME } from '../config/platform';
import { appleTokens, applyBrandColor, normalizeHexColor } from '../theme/appleTokens';
import { getClubStorageItem, setClubStorageItem } from './currentClub';

/**
 * Configuration publique du club courant (GET /ClubConfig/public, anonyme, club de l'en-tête `X-Club` — voir
 * services/currentClub.js) — nom, marque, fuseau, liens stores, textes légaux…
 * Chargée une fois au démarrage (index.js) ; les défauts ci-dessous, neutres (plateforme), s'appliquent tant qu'elle
 * n'est pas reçue ou si l'API est injoignable. Les composants lisent via `useClubPublicConfig()`, le code non-React
 * (formatage de dates) via `clubTimeZone()`.
 */
const DEFAULTS = {
  'club.name': PLATFORM_NAME,
  // Couleur de marque "#rrggbb" (vide = couleur par défaut des tokens) et logos noir (fonds clairs) / blanc (fonds
  // sombres) — vides = logo neutre embarqué.
  'branding.primary_color': '',
  'branding.logo_black_url': '',
  'branding.logo_white_url': '',
  // Liens des stores (réglés au niveau Dev, MySquash) ; vides = boutons des stores masqués.
  'branding.app_store_url': '',
  'branding.play_store_url': '',
  'region.timezone': 'Europe/Brussels',
  // Formats (voir utils/clubFormat.js).
  'region.locale': 'fr-BE',
  'region.currency': 'eur',
  'region.country': 'BE',
  'web.player_app_url': '',
};

// Marque du club (couleur, nom, logos) reçue au dernier chargement, mise en cache par club (localStorage) et appliquée
// DÈS le chargement de ce module, donc avant le premier rendu : pas de flash de la marque par défaut → couleur et
// logo du club à chaque ouverture. Si le serveur renvoie autre chose, c'est appliqué et mis en cache (App.js
// reconstruit alors le thème). L'ancienne clé de la couleur (sans club) est reprise par le premier club qui la lit.
const PRIMARY_COLOR_CACHE_KEY = 'squash_club_primary_color';
const BRANDING_CACHE_KEY = 'courtly.club_branding';
const CACHED_BRANDING_KEYS = ['club.name', 'branding.logo_black_url', 'branding.logo_white_url'];

function readCachedPrimaryColor() {
  return normalizeHexColor(getClubStorageItem(PRIMARY_COLOR_CACHE_KEY)) || '';
}

function writeCachedPrimaryColor(color) {
  // Couleur vide (défaut) mémorisée telle quelle : effacer la clé ferait relire l'ancienne clé sans club.
  setClubStorageItem(PRIMARY_COLOR_CACHE_KEY, color || 'none');
}

function readCachedBranding() {
  try {
    const cached = JSON.parse(getClubStorageItem(BRANDING_CACHE_KEY) || '{}');
    return Object.fromEntries(CACHED_BRANDING_KEYS.filter((k) => typeof cached?.[k] === 'string' && cached[k]).map((k) => [k, cached[k]]));
  } catch {
    return {};
  }
}

function writeCachedBranding(config) {
  setClubStorageItem(BRANDING_CACHE_KEY, JSON.stringify(Object.fromEntries(CACHED_BRANDING_KEYS.map((k) => [k, config[k]]))));
}

/** Barre d'onglets du navigateur sombre : elle suit l'apparence du système (la plupart des navigateurs). */
function browserChromeIsDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** Titre de l'onglet et favicon du club : son nom (sinon la plateforme) et son logo — blanc quand la barre d'onglets
 * est sombre (système en sombre ou ERP en apparence sombre), noir sinon ; à défaut, l'autre, puis l'icône du build. */
function applyDocumentBranding(config) {
  try {
    document.title = config['club.name'] || PLATFORM_NAME;
    const link = document.querySelector('link[rel="icon"]');
    if (!link) return;
    if (link.dataset.defaultHref == null) link.dataset.defaultHref = link.getAttribute('href') || '';
    const black = (config['branding.logo_black_url'] || '').trim();
    const white = (config['branding.logo_white_url'] || '').trim();
    const darkChrome = browserChromeIsDark() || appleTokens.mode === 'dark';
    const href = (darkChrome ? white || black : black || white) || link.dataset.defaultHref;
    if (link.getAttribute('href') !== href) link.setAttribute('href', href);
  } catch {
    /* hors navigateur */
  }
}

let current = { ...DEFAULTS, ...readCachedBranding(), 'branding.primary_color': readCachedPrimaryColor() };
applyBrandColor(current['branding.primary_color']);
applyDocumentBranding(current);
// Apparence du système changée : le favicon suit (logo blanc / noir).
try {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => applyDocumentBranding(current));
} catch {
  /* navigateur ancien / tests */
}
let inflight = null;
// true après la première réponse (ou l'échec) de /ClubConfig/public : les pages publiques gardées par un module
// (écrans, live score) attendent ce chargement avant de lancer leurs requêtes (voir PublicModuleGate, App.js).
let loaded = false;
const loadedListeners = new Set();
function markLoaded() {
  if (loaded) return;
  loaded = true;
  loadedListeners.forEach((l) => l());
}
// ETag de la dernière config reçue : renvoyé en `If-None-Match`, l'API répond 304 (sans corps) si rien n'a changé.
let lastEtag = '';
const listeners = new Set();

function emit() {
  listeners.forEach((l) => l());
}

/** `force` : recharge (après un enregistrement dans ERP → Configuration). */
export function loadClubPublicConfig({ force = false } = {}) {
  if (inflight && !force) return inflight;
  inflight = v2Api
    // skipAuth : endpoint anonyme, jamais de refresh/expiration de session (pages publiques, TV).
    .get('/ClubConfig/public', {
      skipAuth: true,
      headers: lastEtag ? { 'If-None-Match': lastEtag } : undefined,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
    })
    .then(({ status, data, headers }) => {
      // 304 : config inchangée depuis le dernier chargement.
      if (status === 304) {
        markLoaded();
        return current;
      }
      lastEtag = headers?.etag || '';
      const values = data?.values && typeof data.values === 'object' ? data.values : {};
      const next = { ...DEFAULTS };
      Object.entries(values).forEach(([k, v]) => {
        if (typeof v === 'string' && v.trim() !== '') next[k] = v;
      });
      next['branding.primary_color'] = normalizeHexColor(next['branding.primary_color']) || '';
      writeCachedPrimaryColor(next['branding.primary_color']);
      writeCachedBranding(next);
      // Tokens mis à jour avant la notification : les composants qui re-rendent lisent déjà la nouvelle couleur.
      applyBrandColor(next['branding.primary_color']);
      applyDocumentBranding(next);
      // Rechargements périodiques (écrans TV) : pas de nouveau rendu si rien n'a changé.
      if (JSON.stringify(next) === JSON.stringify(current)) {
        markLoaded();
        return current;
      }
      current = next;
      emit();
      markLoaded();
      return current;
    })
    .catch(() => {
      // API injoignable : défauts (modules actifs), comme avant.
      markLoaded();
      return current;
    });
  return inflight;
}

export function getClubPublicConfig() {
  return current;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useClubPublicConfig() {
  return useSyncExternalStore(subscribe, getClubPublicConfig);
}

function subscribeLoaded(listener) {
  loadedListeners.add(listener);
  return () => loadedListeners.delete(listener);
}

/** true une fois la configuration publique reçue (ou son chargement échoué). */
export function useClubPublicConfigLoaded() {
  return useSyncExternalStore(subscribeLoaded, () => loaded);
}

/** Fuseau du club (IANA) pour Intl / toLocale* — lu à chaque appel, donc à jour après chargement. */
export function clubTimeZone() {
  return current['region.timezone'] || DEFAULTS['region.timezone'];
}

/** Couleur de marque du club ("#rrggbb"), ou '' (couleur par défaut des tokens). */
export function clubPrimaryColor(config = current) {
  return normalizeHexColor(config['branding.primary_color']) || '';
}

/**
 * Logo du club selon le fond : `variant` 'black' (logo noir, pour fonds CLAIRS → `branding.logo_black_url`) ou
 * 'white' (logo blanc, pour fonds SOMBRES → `branding.logo_white_url`) ; à défaut, le logo neutre embarqué dans le build.
 * Logos distants de proportions quelconques : les afficher en hauteur fixe avec `objectFit: 'contain'`.
 */
export function clubLogoUrl(config = current, variant = 'black') {
  const key = variant === 'white' ? 'branding.logo_white_url' : 'branding.logo_black_url';
  const url = (config?.[key] || '').trim();
  return url || (variant === 'white' ? bundledLogoWhite : bundledLogoBlack);
}

/**
 * Écrans TV (restent ouverts des heures) : recharge la config publique toutes les `intervalMs` (10 min) et au
 * retour à l'écran, pour qu'un changement de logo / couleur / nom (ERP) ou de modules (Ligue) y arrive sans recharger la page.
 * Onglet masqué : pas de polling ; à son retour, rechargement immédiat (au plus une fois par minute) puis reprise.
 */
export function useClubPublicConfigPolling(intervalMs = 10 * 60 * 1000) {
  useEffect(() => {
    let id = null;
    let lastRefreshAt = Date.now();
    const refresh = () => {
      lastRefreshAt = Date.now();
      loadClubPublicConfig({ force: true });
    };
    const stop = () => {
      if (id != null) window.clearInterval(id);
      id = null;
    };
    const start = () => {
      stop();
      if (document.visibilityState !== 'hidden') id = window.setInterval(refresh, intervalMs);
    };
    const onVisible = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      if (Date.now() - lastRefreshAt > 60000) refresh();
      start();
    };
    start();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [intervalMs]);
}

/** Schéma de deep link de l'app mobile du club (env `REACT_APP_MOBILE_SCHEME`, défaut "my22"). */
export const MOBILE_APP_SCHEME = (process.env.REACT_APP_MOBILE_SCHEME || '').trim() || 'my22';
